import { z } from 'zod';

export const ARTIST_TYPES = ['SOLO', 'GROUP'] as const;
export const ARTIST_GENDERS = [
  'MALE',
  'FEMALE',
  'MIXED',
  'NON_BINARY',
  'NOT_DISCLOSED',
] as const;
export const ALIAS_LANGUAGES = ['KO', 'EN', 'JA', 'ZH', 'ES', 'FR'] as const;
export const MEMBER_ROLES = [
  'LEADER',
  'VOCAL',
  'RAPPER',
  'DANCER',
  'VISUAL',
  'MAKNAE',
  'MEMBER',
] as const;
export const ARTIST_IMAGE_TYPES = ['PROFILE', 'BANNER', 'GALLERY'] as const;

const today = (): string => new Date().toISOString().slice(0, 10);

const aliasInputSchema = z.object({
  language: z.enum(ALIAS_LANGUAGES),
  aliasValue: z
    .string()
    .trim()
    .min(1, { message: 'artists.errors.aliasValueRequired' })
    .max(100, { message: 'artists.errors.aliasValueTooLong' }),
  isPrimary: z.boolean().optional(),
});

export const artistCreateSchema = z
  .object({
    type: z.enum(ARTIST_TYPES),
    stageName: z
      .string()
      .trim()
      .min(1, { message: 'artists.errors.stageNameRequired' })
      .max(100, { message: 'artists.errors.stageNameTooLong' }),
    legalName: z
      .string()
      .trim()
      .max(100, { message: 'artists.errors.legalNameTooLong' })
      .optional()
      .or(z.literal('')),
    gender: z.enum(ARTIST_GENDERS).optional().nullable(),
    nationality: z
      .string()
      .trim()
      .max(80, { message: 'artists.errors.nationalityTooLong' })
      .optional()
      .or(z.literal('')),
    debutDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'artists.errors.dateFormat' })
      .optional()
      .or(z.literal('')),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'artists.errors.dateFormat' })
      .optional()
      .or(z.literal('')),
    fanClubName: z
      .string()
      .trim()
      .max(80, { message: 'artists.errors.fanClubNameTooLong' })
      .optional()
      .or(z.literal('')),
    agencyName: z
      .string()
      .trim()
      .max(100, { message: 'artists.errors.agencyNameTooLong' })
      .optional()
      .or(z.literal('')),
    bio: z
      .string()
      .max(4000, { message: 'artists.errors.bioTooLong' })
      .optional()
      .or(z.literal('')),
    initialAliases: z.array(aliasInputSchema).max(20).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.debutDate && data.debutDate > today()) {
      ctx.addIssue({
        code: 'custom',
        path: ['debutDate'],
        message: 'artists.errors.debutDateFuture',
      });
    }
    if (data.type === 'GROUP' && data.birthDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['birthDate'],
        message: 'artists.errors.groupBirthDateForbidden',
      });
    }
  });

export type ArtistCreateValues = z.infer<typeof artistCreateSchema>;

export const emptyArtistCreateValues = (): ArtistCreateValues => ({
  type: 'SOLO',
  stageName: '',
  legalName: '',
  gender: null,
  nationality: '',
  debutDate: '',
  birthDate: '',
  fanClubName: '',
  agencyName: '',
  bio: '',
  initialAliases: [],
});

export const artistUpdateSchema = z
  .object({
    stageName: z
      .string()
      .trim()
      .min(1, { message: 'artists.errors.stageNameRequired' })
      .max(100, { message: 'artists.errors.stageNameTooLong' })
      .optional(),
    legalName: z.string().trim().max(100).optional().or(z.literal('')),
    gender: z.enum(ARTIST_GENDERS).optional().nullable(),
    nationality: z.string().trim().max(80).optional().or(z.literal('')),
    debutDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'artists.errors.dateFormat' })
      .optional()
      .or(z.literal('')),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'artists.errors.dateFormat' })
      .optional()
      .or(z.literal('')),
    fanClubName: z.string().trim().max(80).optional().or(z.literal('')),
    agencyName: z.string().trim().max(100).optional().or(z.literal('')),
    bio: z.string().max(4000).optional().or(z.literal('')),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined && v !== ''), {
    message: 'artists.errors.updateMinOne',
  });

export type ArtistUpdateValues = z.infer<typeof artistUpdateSchema>;

export const artistAliasSchema = aliasInputSchema;
export type ArtistAliasValues = z.infer<typeof artistAliasSchema>;

export const artistMemberAddSchema = z.object({
  memberArtistId: z
    .string()
    .uuid({ message: 'artists.errors.memberArtistIdInvalid' }),
  role: z.enum(MEMBER_ROLES),
  joinedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'artists.errors.dateFormat' }),
});
export type ArtistMemberAddValues = z.infer<typeof artistMemberAddSchema>;

export const ARTIST_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const ARTIST_IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export const artistImageUploadSchema = z.object({
  type: z.enum(ARTIST_IMAGE_TYPES),
  sortOrder: z.number().int().min(0).optional(),
  altText: z.string().max(200).optional().or(z.literal('')),
});
export type ArtistImageUploadValues = z.infer<typeof artistImageUploadSchema>;
